import { isEqual } from 'lodash-es'
import { type FormEvent, useState } from 'react'
import { toast } from 'react-toastify'

import { Accordion, TextField } from '@loadsmart/loadsmart-ui'
import type Status from '@loadsmart/loadsmart-ui/dist/utils/types/Status'
import { Field, Text, Layout, Tag, Button } from '@loadsmart/miranda-react'

import TruckFacilityIllustration from 'core/ui/assets/illustration-truck-facility.svg'
import { AnimatedArrowIcon } from 'core/ui/components/animated-arrow-icon/AnimatedArrowIcon'

import { type EffectiveCarrier } from '../domain/Shipment'
import { createAssetCarrier } from '../use-cases/createAssetCarrier'
import { updateAssetCarrier } from '../use-cases/updateAssetCarrier'

export type AssetCarrierSelectorProps = {
  effectiveCarrier?: EffectiveCarrier
  shipmentUUID: string
  onAssignAssetCarrier: () => void
}

export type AssetCarrierForm = {
  name?: string
  mc?: string
  dot?: string
}

export function AssetCarrierSelector({
  effectiveCarrier,
  shipmentUUID,
  onAssignAssetCarrier,
}: AssetCarrierSelectorProps) {
  const [isOpened, setIsOpened] = useState(false)
  const [touched, setTouched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [assetCarrierForm, setAssetCarrierForm] = useState({
    name: effectiveCarrier?.name || '',
    mc: effectiveCarrier?.mc,
    dot: effectiveCarrier?.dot,
  })
  const isInvalid = touched && !assetCarrierForm.name
  const disableSaveBtn = !assetCarrierForm.name || isEqual(effectiveCarrier, assetCarrierForm)

  function handleExpandChange() {
    setIsOpened(!isOpened)
  }

  function handleFieldChange(key: keyof AssetCarrierForm, value: string) {
    setAssetCarrierForm({ ...assetCarrierForm, [key]: value })
    setTouched(true)
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setLoading(true)
    try {
      if (effectiveCarrier) {
        await updateAssetCarrier({ shipmentUUID, effectiveCarrier: assetCarrierForm })
      } else {
        await createAssetCarrier({ shipmentUUID, effectiveCarrier: assetCarrierForm })
      }

      toast.success('Asset carrier assigned with success')
      onAssignAssetCarrier()
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Accordion expanded={isOpened} onExpandedChange={handleExpandChange}>
      <Accordion.Toggle trailing={AnimatedArrowIcon}>
        <Layout.Group align="center" gap="spacing-2">
          <Text variant="heading-sm">Select asset carrier</Text>
          {effectiveCarrier ? (
            <Text variant="heading-sm">- {effectiveCarrier.name}</Text>
          ) : (
            <Tag size="small" variant="warning">
              Pending
            </Tag>
          )}
        </Layout.Group>
      </Accordion.Toggle>
      <Accordion.Body>
        <form onSubmit={onSubmit}>
          <Layout.Group>
            <img
              width={185}
              src={TruckFacilityIllustration}
              alt="Truck facility"
              style={{ alignSelf: 'center' }}
            />
            <Layout.Stack align="flex-start" style={{ flex: 1 }}>
              <Field required status={isInvalid ? 'danger' : 'default'} style={{ width: '100%' }}>
                <Field.Label id="asset-carrier-name">Asset carrier's name</Field.Label>
                <TextField
                  aria-labelledby="asset-carrier-name"
                  name="name"
                  defaultValue={assetCarrierForm.name}
                  onChange={({ target }) => handleFieldChange('name', target.value)}
                  status={isInvalid ? ('danger' as Status) : ('neutral' as Status)}
                />
                <Field.Hint>
                  {isInvalid
                    ? 'Required field'
                    : 'We include both the names of the asset carrier and the broker on the Bill of Lading (BOL)'}
                </Field.Hint>
              </Field>
              <Layout.Stack gap="spacing-1">
                <Layout.Group>
                  <Field>
                    <Field.Label id="dot">DOT</Field.Label>
                    <TextField
                      aria-labelledby="dot"
                      name="dot"
                      defaultValue={assetCarrierForm.dot}
                      onChange={({ target }) => handleFieldChange('dot', target.value)}
                    />
                  </Field>
                  <Field>
                    <Field.Label id="mc-number">MC number</Field.Label>
                    <TextField
                      aria-labelledby="mc-number"
                      name="mc"
                      defaultValue={assetCarrierForm.mc}
                      onChange={({ target }) => handleFieldChange('mc', target.value)}
                    />
                  </Field>
                </Layout.Group>
                <Field>
                  <Field.Hint>
                    We use the asset carrier's DOT and MC number to enhance our tracking
                    capabilities
                  </Field.Hint>
                </Field>
              </Layout.Stack>
              <Button type="submit" variant="primary" disabled={disableSaveBtn} loading={loading}>
                Assign asset carrier and save
              </Button>
            </Layout.Stack>
          </Layout.Group>
        </form>
      </Accordion.Body>
    </Accordion>
  )
}
